/**
 * Fires `medical_necessity_button_shown` once when the Medical Necessity
 * button renders on the single-order (CPT) screen.
 * @module useMedicalNecessityButtonShownTracking
 */
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { GeneralOrderDetails } from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";
import { EventsName } from "../../../enums/eventsName";
import { RootState } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import { apiWorkerManager } from "../../../workerManagers/workerManager";

/** Inputs for {@link useMedicalNecessityButtonShownTracking}. */
export interface UseMedicalNecessityButtonShownTrackingArgs {
  order: GeneralOrderDetails | null;
  isButtonVisible: boolean;
  isGateResolved: boolean;
  regimenName: string | null;
}

/**
 * Emits `medical_necessity_button_shown` once per view of an order when the
 * button renders (last-order-id dedup; revisiting an earlier order re-fires).
 *
 * Attributes: `order_id`, `org`, `regimen_name`, `assigned_to`. Optional
 * fields are omitted when empty.
 *
 * @param args - Order, gate resolution, and visibility flags
 */
export const useMedicalNecessityButtonShownTracking = ({
  order,
  isButtonVisible,
  isGateResolved,
  regimenName,
}: UseMedicalNecessityButtonShownTrackingArgs): void => {
  const user = useSelector(
    (state: RootState) => state.firebaseAuthentication.user,
  );
  // Once per view: tracks only the last order id, so A→B→A re-fires for A.
  const loggedOrderIdRef = useRef<string | null>(null);

  useEffect(() => {
    const orderId = order?.order_id;
    if (!isGateResolved || !isButtonVisible || !orderId) return;
    if (loggedOrderIdRef.current === orderId) return;
    loggedOrderIdRef.current = orderId;

    const org = getOrgIdForFetchExternalWorklist() || order.org_id || "";
    const resolvedRegimen =
      regimenName || order.payload?.regimen_name || undefined;
    const assignedTo = order.assigned_to?.assigned_to_name || undefined;

    const additionalData: Record<string, unknown> = {
      screen_name: ScreenNamesForBigQuery.CPT_CODE_SCREEN,
      org,
    };
    if (resolvedRegimen) additionalData.regimen_name = resolvedRegimen;
    if (assignedTo) additionalData.assigned_to = assignedTo;

    apiWorkerManager.enqueueAndProcess({
      patient_id:
        order.demographics?.patient_id ?? order.demographics?.patient_mrn ?? "",
      order_id: orderId,
      org_id: org,
      user_id: user?.id ?? "",
      user_email: user?.email ?? "",
      event_name: EventsName.MEDICAL_NECESSITY_BUTTON_SHOWN,
      additional_data: additionalData,
    });
  }, [
    order,
    isButtonVisible,
    isGateResolved,
    regimenName,
    user?.id,
    user?.email,
  ]);
};
